import {
  DataProvider,
  GetListResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
  CreateParams,
  CreateResult,
  Identifier,
  DeleteParams,
  DeleteResult,
} from 'react-admin'
import { TicketAPIGetListParams } from '../../common/ticket-api-get-list.params'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../../common/get-by-conditions.utils'
import filterMapper from './engine-media-pack-filter.mapper'
import { mapSortEngineMediaPackParam } from './engine-media-pack-sort.mapper'
import {
  get,
  getWithPagination,
  patch,
  post,
  put,
  remove,
} from '../../common/fetch.utils'
import { EngineMediaPackDto } from '../../dto/engine-media/engine-media-pack.dto'
import { ENGINE_MEDIA_PACKS_URL } from '../../api-urls'
import { CacheableDataProviderExtensionResult } from '../../common/data-provider'

const provider = {
  getList: async (
    resource: string,
    { filter, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<EngineMediaPackDto>> => {
    const filterParams = `o=>${filterParamsComposer('o', filter, filterMapper)}`
    const pathParams = queryParamsComposer(
      sort,
      pagination,
      mapSortEngineMediaPackParam,
    )
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<EngineMediaPackDto[]>(
      `${ENGINE_MEDIA_PACKS_URL}/GetByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<EngineMediaPackDto>> => {
    const data = await get<EngineMediaPackDto>(ENGINE_MEDIA_PACKS_URL, `/${id}`)
    return Promise.resolve({
      data,
    })
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<EngineMediaPackDto>> => {
    const { data } = await getWithPagination<EngineMediaPackDto[]>(
      `${ENGINE_MEDIA_PACKS_URL}/GetByConditions/`,
      `e=>new int[] {${ids.toString()}}.Contains(e.Id)`,
    )
    return Promise.resolve({
      data,
    })
  },
  create: async (
    resource: string,
    { data }: CreateParams<CreateEngineMediaPackRequest>,
  ): Promise<CreateResult<EngineMediaPackDto>> => {
    const pack = await post<
      CreateEngineMediaPackRequest[],
      EngineMediaPackDto[]
    >(ENGINE_MEDIA_PACKS_URL, [data])
    return {
      data: pack[0],
    }
  },
  update: async (
    resource: string,
    { id, data, previousData }: UpdateParams<EngineMediaPackDto>,
  ): Promise<UpdateResult> => {
    const update = await patch<
      { id: Identifier; [n: string]: any },
      EngineMediaPackDto
    >(ENGINE_MEDIA_PACKS_URL, {
      id,
      description:
        data.description !== previousData.description
          ? data.description
          : undefined,
    })
    return Promise.resolve({ data: update })
  },
  delete: async (
    resource: string,
    { id }: DeleteParams,
  ): Promise<DeleteResult> => {
    await remove<undefined>(ENGINE_MEDIA_PACKS_URL, `/Delete/${id}`)
    return Promise.resolve({ data: { id } })
  },
  attachFiles: async (
    resource: string,
    params: PacksFilesDto,
  ): Promise<CacheableDataProviderExtensionResult<PacksFilesDto>> => {
    const packFiles = await put<PacksFilesDto, PacksFilesDto>(
      `${ENGINE_MEDIA_PACKS_URL}/AttachFiles`,
      params,
    )
    return {
      data: packFiles,
    }
  },
  detachFiles: async (
    resource: string,
    params: PacksFilesDto,
  ): Promise<CacheableDataProviderExtensionResult<PacksFilesDto>> => {
    const packFiles = await put<PacksFilesDto, PacksFilesDto>(
      `${ENGINE_MEDIA_PACKS_URL}/DetachFiles`,
      params,
    )
    return {
      data: packFiles,
    }
  },
} as PacksDataProvider

interface PacksDataProvider extends DataProvider {
  attachFiles: (
    resource: string,
    params: PacksFilesDto,
  ) => Promise<CacheableDataProviderExtensionResult<PacksFilesDto>>

  detachFiles: (
    resource: string,
    params: PacksFilesDto,
  ) => Promise<CacheableDataProviderExtensionResult<PacksFilesDto>>
}

interface PacksFilesDto {
  readonly engineMediaPackId: number
  readonly engineMediaFilesIds: number[]
}

export interface CreateEngineMediaPackRequest {
  readonly description: string
}

export default provider
